import React, { useState, useEffect, useCallback } from 'react';
import { productsApi } from '../../services/api';
import AdminPageHeader from '../../components/AdminPageHeader';
import './Products.css';

const CATEGORIES = [
  { value: 'cc_bev', label: 'CC Beverages', company: 'CC' },
  { value: 'cc_veg', label: 'CC Fermented Veg', company: 'CC' },
  { value: 'cc_brine', label: 'CC Brines', company: 'CC' },
  { value: 'cc_keg', label: 'CC Kegs', company: 'CC' },
  { value: 'ar_frozen', label: 'AR Frozen', company: 'AR' },
  { value: 'ar_refrigerated', label: 'AR Refrigerated', company: 'AR' },
];

const EMPTY_FORM = {
  name: '', company: 'CC', category: 'cc_bev',
  size: '', unit: '', wholesale_price: '', is_active: true,
};

function formatPrice(val) {
  if (val === '' || val === null || val === undefined) return '-';
  const n = parseFloat(val);
  return isNaN(n) ? '-' : `$${n.toFixed(2)}`;
}

function companyBadge(company) {
  return <span className={`company-badge ${company === 'CC' ? 'badge-cc' : 'badge-ar'}`}>{company}</span>;
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterActive, setFilterActive] = useState('active');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await productsApi.getAll();
      setProducts(data.products || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    const next = { ...form, [name]: type === 'checkbox' ? checked : value };
    if (name === 'company') {
      const firstCat = CATEGORIES.find((c) => c.company === value);
      if (firstCat) next.category = firstCat.value;
    }
    setForm(next);
  }

  function openNew() { setForm(EMPTY_FORM); setEditing({}); }
  function openEdit(product) { setForm({ ...product, wholesale_price: product.wholesale_price ?? '' }); setEditing(product); }
  function closeForm() { setEditing(null); setForm(EMPTY_FORM); }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, wholesale_price: form.wholesale_price === '' ? null : parseFloat(form.wholesale_price) };
      if (editing?.id) { await productsApi.update(payload); }
      else { await productsApi.create(payload); }
      await load();
      closeForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(product) {
    try {
      await productsApi.toggleActive(product.id, !product.is_active);
      setProducts((ps) => ps.map((p) => p.id === product.id ? { ...p, is_active: !p.is_active } : p));
    } catch (err) {
      setError(err.message);
    }
  }

  const filtered = products.filter((p) => {
    if (filterCompany !== 'all' && p.company !== filterCompany) return false;
    if (filterCategory !== 'all' && p.category !== filterCategory) return false;
    if (filterActive === 'active' && !p.is_active) return false;
    if (filterActive === 'inactive' && p.is_active) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const availableCategories = filterCompany === 'all' ? CATEGORIES : CATEGORIES.filter((c) => c.company === filterCompany);
  const formCategories = CATEGORIES.filter((c) => c.company === form.company);

  return (
    <div className="page">
      <AdminPageHeader
        title="Products"
        subtitle={`${products.filter((p) => p.is_active).length} active SKUs`}
        action={<button className="btn-primary" onClick={openNew}>+ Add Product</button>}
      />
      <div className="page-body">
        {error && <div className="error-banner">{error} <button onClick={load}>Retry</button></div>}
        <div className="filter-bar products-filters">
          <input className="search-input" placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} />
          <div className="filter-group">
            {['all', 'CC', 'AR'].map((c) => (
              <button key={c} className={`filter-btn ${filterCompany === c ? 'active' : ''}`}
                onClick={() => { setFilterCompany(c); setFilterCategory('all'); }}>
                {c === 'all' ? 'All' : c}
              </button>
            ))}
          </div>
          <div className="filter-group">
            <button className={`filter-btn ${filterCategory === 'all' ? 'active' : ''}`} onClick={() => setFilterCategory('all')}>All Categories</button>
            {availableCategories.map((c) => (
              <button key={c.value} className={`filter-btn ${filterCategory === c.value ? 'active' : ''}`} onClick={() => setFilterCategory(c.value)}>
                {c.label}
              </button>
            ))}
          </div>
          <div className="filter-group">
            {['active', 'inactive', 'all'].map((f) => (
              <button key={f} className={`filter-btn ${filterActive === f ? 'active' : ''}`} onClick={() => setFilterActive(f)}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="loading-state">Loading products...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">{products.length === 0 ? 'No products yet.' : 'No products match these filters.'}</div>
        ) : (
          <div className="products-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product</th><th>Co.</th><th>Category</th>
                  <th>Size</th><th>Unit</th><th>Wholesale Price</th>
                  <th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className={p.is_active ? '' : 'row-inactive'}>
                    <td className="td-name">{p.name}</td>
                    <td>{companyBadge(p.company)}</td>
                    <td className="td-cat">{CATEGORIES.find((c) => c.value === p.category)?.label || p.category}</td>
                    <td>{p.size || '-'}</td>
                    <td>{p.unit || '-'}</td>
                    <td className="td-price">{formatPrice(p.wholesale_price)}</td>
                    <td>
                      <button className={`status-toggle ${p.is_active ? 'active' : 'inactive'}`} onClick={() => toggleActive(p)}>
                        {p.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td><button className="btn-edit" onClick={() => openEdit(p)}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="table-footer">{filtered.length} products shown</div>
          </div>
        )}
      </div>
      {editing !== null && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing?.id ? 'Edit Product' : 'Add Product'}</h2>
              <button className="modal-close" onClick={closeForm}>x</button>
            </div>
            <form onSubmit={handleSave} className="modal-form">
              <div className="field">
                <label>Product Name *</label>
                <input name="name" value={form.name} onChange={handleChange} required placeholder="e.g. Hibiscus Kombucha" autoFocus />
              </div>
              <div className="field-row">
                <div className="field">
                  <label>Company *</label>
                  <select name="company" value={form.company} onChange={handleChange}>
                    <option value="CC">Collective Culture</option>
                    <option value="AR">Apple Ridge Farm</option>
                  </select>
                </div>
                <div className="field">
                  <label>Category *</label>
                  <select name="category" value={form.category} onChange={handleChange}>
                    {formCategories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="field-row">
                <div className="field">
                  <label>Size</label>
                  <input name="size" value={form.size} onChange={handleChange} placeholder="e.g. 12oz" />
                </div>
                <div className="field">
                  <label>Unit</label>
                  <input name="unit" value={form.unit} onChange={handleChange} placeholder="e.g. can, jar" />
                </div>
              </div>
              <div className="field">
                <label>Wholesale Price</label>
                <div className="price-input-wrap">
                  <span className="price-prefix">$</span>
                  <input name="wholesale_price" type="number" step="0.01" min="0"
                    value={form.wholesale_price} onChange={handleChange} placeholder="0.00" className="price-input" />
                </div>
              </div>
              <div className="field">
                <label className="checkbox-label">
                  <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} />
                  Active
                </label>
              </div>
              {error && <div className="form-error">{error}</div>}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeForm}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editing?.id ? 'Save Changes' : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
